import React from "react";

const UNBOUNDED_SCROLL_EPSILON = 4;
const MAX_SCROLL_PARENT_ATTACH_FRAMES = 120;
const VIRTUAL_ROW_INDEX_ATTR = "data-virtual-index";

type ScrollAlign = "auto" | "start" | "center" | "end" | "belowHeader";

/**
 * When the scroll element has no real vertical scroll range (e.g. overflow not clipped),
 * clientHeight ≈ scrollHeight and the virtualizer would render all rows. Cap the viewport
 * using the window so we never mount thousands of rows in one frame.
 */
function readEffectiveClientHeight(el: HTMLElement, rowCount: number): number {
    const ch = el.clientHeight || 0;
    const sh = el.scrollHeight || 0;
    if (rowCount > 50 && sh - ch < UNBOUNDED_SCROLL_EPSILON) {
        return Math.max(200, Math.floor((typeof window !== "undefined" ? window.innerHeight : 600) * 0.5));
    }
    return ch;
}

function syncVerticalScrollMetrics(
    el: HTMLElement,
    rowCount: number,
    scrollTopRef: React.MutableRefObject<number>,
    clientHeightRef: React.MutableRefObject<number>,
    force: () => void,
) {
    const st = el.scrollTop;
    const effectiveCh = readEffectiveClientHeight(el, rowCount);
    if (st !== scrollTopRef.current || effectiveCh !== clientHeightRef.current) {
        scrollTopRef.current = st;
        clientHeightRef.current = effectiveCh;
        force();
    }
}

function subscribeToVerticalScrollParent(
    getScrollParent: () => HTMLElement | null,
    rowCount: number,
    scrollTopRef: React.MutableRefObject<number>,
    clientHeightRef: React.MutableRefObject<number>,
    scheduledRef: React.MutableRefObject<boolean>,
    force: () => void,
): () => void {
    let rafId = 0;
    let cancelled = false;
    let attachedEl: HTMLElement | null = null;
    let framesWaited = 0;

    const detach = () => {
        if (!attachedEl) return;
        attachedEl.removeEventListener("scroll", onScroll);
        ro.disconnect();
        attachedEl = null;
    };

    const onScroll = () => {
        if (!attachedEl) return;
        if (!scheduledRef.current) {
            scheduledRef.current = true;
            requestAnimationFrame(() => {
                scheduledRef.current = false;
                if (attachedEl) syncVerticalScrollMetrics(attachedEl, rowCount, scrollTopRef, clientHeightRef, force);
            });
        }
    };

    const onResize = () => {
        if (attachedEl) syncVerticalScrollMetrics(attachedEl, rowCount, scrollTopRef, clientHeightRef, force);
    };

    const ro = new ResizeObserver(onResize);

    const attach = (el: HTMLElement) => {
        detach();
        attachedEl = el;
        scrollTopRef.current = el.scrollTop;
        clientHeightRef.current = readEffectiveClientHeight(el, rowCount);
        force();
        el.addEventListener("scroll", onScroll, {passive: true});
        ro.observe(el);
    };

    const tryAttach = () => {
        if (cancelled) return;
        const el = getScrollParent();
        if (el) {
            attach(el);
            return;
        }
        framesWaited += 1;
        if (framesWaited < MAX_SCROLL_PARENT_ATTACH_FRAMES) {
            rafId = requestAnimationFrame(tryAttach);
        }
    };

    tryAttach();

    return () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
        detach();
        ro.disconnect();
    };
}

function subscribeToHorizontalScrollParent(
    getScrollParent: () => HTMLElement | null,
    scrollLeftRef: React.MutableRefObject<number>,
    clientWidthRef: React.MutableRefObject<number>,
    scheduledRef: React.MutableRefObject<boolean>,
    force: () => void,
): () => void {
    let rafId = 0;
    let cancelled = false;
    let attachedEl: HTMLElement | null = null;
    let framesWaited = 0;

    const detach = () => {
        if (!attachedEl) return;
        attachedEl.removeEventListener("scroll", onScroll);
        ro.disconnect();
        attachedEl = null;
    };

    const sync = (el: HTMLElement) => {
        const sl = el.scrollLeft;
        const w = el.clientWidth || 0;
        if (sl !== scrollLeftRef.current || w !== clientWidthRef.current) {
            scrollLeftRef.current = sl;
            clientWidthRef.current = w;
            force();
        }
    };

    const onScroll = () => {
        if (!attachedEl) return;
        if (!scheduledRef.current) {
            scheduledRef.current = true;
            requestAnimationFrame(() => {
                scheduledRef.current = false;
                if (attachedEl) sync(attachedEl);
            });
        }
    };

    const onResize = () => {
        if (attachedEl) sync(attachedEl);
    };

    const ro = new ResizeObserver(onResize);

    const attach = (el: HTMLElement) => {
        detach();
        attachedEl = el;
        scrollLeftRef.current = el.scrollLeft;
        clientWidthRef.current = el.clientWidth || 0;
        force();
        el.addEventListener("scroll", onScroll, {passive: true});
        ro.observe(el);
    };

    const tryAttach = () => {
        if (cancelled) return;
        const el = getScrollParent();
        if (el) {
            attach(el);
            return;
        }
        framesWaited += 1;
        if (framesWaited < MAX_SCROLL_PARENT_ATTACH_FRAMES) {
            rafId = requestAnimationFrame(tryAttach);
        }
    };

    tryAttach();

    return () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
        detach();
        ro.disconnect();
    };
}

/**
 * Virtualizer for fixed-height rows.
 */
export function useFixedRowVirtualizer<T>(args: {
    data: T[];
    rowHeight: number;
    overscan?: number;
    getScrollParent: () => HTMLElement | null;
    getItemKey?: (item: T, index: number) => string | number;
    /**
     * Scroll-content height above the virtualized list (e.g. titles within the same scroll parent).
     * Rows are culled only after the user scrolls this far past the list start.
     */
    offsetStartingHeight?: number;
    /**
     * Scroll-content height below the virtualized list that reduces the effective viewport bottom.
     */
    offsetEndingHeight?: number;
}) {
    const {data, rowHeight, getScrollParent, getItemKey} = args;
    const overscan = Math.max(0, args.overscan ?? 5);
    const offsetStartingHeight = args.offsetStartingHeight ?? 0;
    const offsetEndingHeight = args.offsetEndingHeight ?? 0;
    const count = data.length;
    const totalHeight = rowHeight * count;
    const scrollTopRef = React.useRef(0);
    const clientHeightRef = React.useRef(0);
    const scheduledRef = React.useRef(false);
    const [, force] = React.useReducer((x) => x + 1, 0);

    React.useEffect(() => {
        return subscribeToVerticalScrollParent(
            getScrollParent,
            count,
            scrollTopRef,
            clientHeightRef,
            scheduledRef,
            force,
        );
    }, [getScrollParent, count]);

    const startPx = Math.max(0, scrollTopRef.current - offsetStartingHeight);
    const endPx = Math.min(
        totalHeight,
        scrollTopRef.current + clientHeightRef.current - offsetStartingHeight - offsetEndingHeight,
    );
    const startIndex = Math.floor(startPx / rowHeight);
    const endIndexByPx = Math.min(count, Math.ceil(endPx / rowHeight));
    const startIndexWithScan = Math.max(0, startIndex - overscan);
    const endIndexWithScan = Math.min(count, endIndexByPx + overscan);
    const items = React.useMemo(() => data.slice(startIndexWithScan, endIndexWithScan), [data, startIndexWithScan, endIndexWithScan]);
    const topSpacerHeight = startIndexWithScan * rowHeight;
    const renderedHeight = (endIndexWithScan - startIndexWithScan) * rowHeight;
    const bottomSpacerHeight = Math.max(0, totalHeight - topSpacerHeight - renderedHeight);
    const keyFor = (visibleIndex: number) => {
        const absoluteIndex = startIndexWithScan + visibleIndex;
        return getItemKey ? getItemKey(data[absoluteIndex], absoluteIndex) : absoluteIndex;
    };
    return {
        items,
        startIndex: startIndexWithScan,
        endIndex: endIndexWithScan,
        topSpacerHeight,
        bottomSpacerHeight,
        totalHeight,
        keyFor,
    } as const;
}

/**
 * Dynamic-height virtualizer for TABLE rows.
 */
export function useDynamicRowVirtualizer<T>(args: {
    data: T[];
    estimateRowHeight: number;
    /** When false, returns all rows with no spacers and no scroll subscription. Default true. */
    enabled?: boolean;
    /** Floor for measured and estimated row heights (px). */
    minRowHeight?: number;
    overscan?: number;
    getScrollParent: () => HTMLElement | null;
    getItemKey?: (item: T, index: number) => string | number;
    getGroupKey?: (item: T, index: number) => string | number | undefined;
    onRangeChange?: (info: {
        startIndex: number;
        endIndex: number;
        startPx: number;
        endPx: number;
        totalHeight: number;
    }) => void;
    stickyHeaderHeight?: number;
    stickyFooterHeight?: number;
    /**
     * Scroll-content height above the virtualized list (e.g. titles within the same scroll parent).
     * Rows are culled only after the user scrolls this far past the list start.
     */
    offsetStartingHeight?: number;
    /**
     * Scroll-content height below the virtualized list that reduces the effective viewport bottom.
     */
    offsetEndingHeight?: number;
    /**
     * Bumps when externally locked row heights change.
     * Locked heights are copied into the offset table on that same render.
     */
    heightRevision?: number;
    /**
     * Locked height for a row, when column virtualization has measured cells that are not mounted.
     * Return null to keep the estimate or the live DOM height.
     */
    getRowHeight?: (item: T, index: number) => number | null | undefined;
    /**
     * When this value changes, measured heights reset to the estimate before locked heights are applied.
     * Used when density changes and every cached cell height is stale.
     */
    heightResetToken?: unknown;
    /**
     * When true, a live row measurement may raise a row but cannot shrink it below {@link getRowHeight}.
     */
    preserveLockedRowHeight?: boolean;
}) {
    const {
        data,
        estimateRowHeight,
        getScrollParent,
        getItemKey,
        getGroupKey,
        onRangeChange,
        stickyHeaderHeight = 0,
        stickyFooterHeight = 0,
        offsetStartingHeight = 0,
        offsetEndingHeight = 0,
    } = args;
    const enabled = args.enabled ?? true;
    const minRowHeight = Math.max(1, args.minRowHeight ?? 1);
    const effectiveEstimate = Math.max(minRowHeight, estimateRowHeight);
    const overscan = Math.max(0, args.overscan ?? 5);
    const count = data.length;

    const heightsRef = React.useRef<number[]>([]);
    const offsetsRef = React.useRef<number[]>([]);
    const totalHeightRef = React.useRef<number>(0);
    const scrollTopRef = React.useRef(0);
    const clientHeightRef = React.useRef(0);
    const scheduledRef = React.useRef(false);
    const scrollParentRef = React.useRef<HTMLElement | null>(null);
    const rowObserversByIndexRef = React.useRef<Map<number, ResizeObserver>>(new Map());
    const rowElementsByIndexRef = React.useRef<Map<number, HTMLElement>>(new Map());
    const dataRef = React.useRef(data);
    dataRef.current = data;
    const getRowHeightRef = React.useRef(args.getRowHeight);
    getRowHeightRef.current = args.getRowHeight;
    const preserveLockRef = React.useRef(args.preserveLockedRowHeight ?? false);
    preserveLockRef.current = args.preserveLockedRowHeight ?? false;
    const externalRevisionRef = React.useRef(-1);
    const resetTokenRef = React.useRef(args.heightResetToken);
    const lastEstimateRef = React.useRef(args.estimateRowHeight);
    const [, force] = React.useReducer((x) => x + 1, 0);

    const keyToIndexRef = React.useRef<Map<string | number, number>>(new Map());
    React.useEffect(() => {
        const m = new Map<string | number, number>();
        for (let i = 0; i < data.length; i++) {
            const k = getItemKey ? getItemKey(data[i], i) : i;
            m.set(k, i);
        }
        keyToIndexRef.current = m;
    }, [data, getItemKey]);

    const groupKeyToFirstIndexRef = React.useRef<Map<string | number, number>>(new Map());
    React.useEffect(() => {
        const m = new Map<string | number, number>();
        if (getGroupKey) {
            for (let i = 0; i < data.length; i++) {
                const g = getGroupKey(data[i], i);
                if (g == null) continue;
                if (!m.has(g)) m.set(g, i);
            }
        }
        groupKeyToFirstIndexRef.current = m;
    }, [data, getGroupKey]);

    const ensureArrays = React.useCallback(() => {
        if (heightsRef.current.length !== count) {
            const next = new Array(count).fill(effectiveEstimate);
            for (let i = 0; i < heightsRef.current.length && i < count; i++) {
                const prev = heightsRef.current[i] || effectiveEstimate;
                next[i] = Math.max(minRowHeight, prev);
            }
            heightsRef.current = next;
        }
        if (offsetsRef.current.length !== count) {
            offsetsRef.current = new Array(count).fill(0);
        }
    }, [count, effectiveEstimate, minRowHeight]);

    const rebuildOffsets = React.useCallback(() => {
        const h = heightsRef.current;
        const off = offsetsRef.current;
        let sum = 0;
        for (let i = 0; i < h.length; i++) {
            off[i] = sum;
            sum += h[i] || 0;
        }
        totalHeightRef.current = sum;
    }, []);

    const sumRange = (start: number, end: number) => {
        const h = heightsRef.current;
        let s = 0;
        for (let i = start; i < end; i++) s += h[i] || 0;
        return s;
    };

    const findIndexAtOffset = (y: number) => {
        const off = offsetsRef.current;
        let lo = 0;
        let hi = off.length - 1;
        let ans = 0;
        while (lo <= hi) {
            const mid = (lo + hi) >>> 1;
            if (off[mid] <= y) {
                ans = mid;
                lo = mid + 1;
            } else {
                hi = mid - 1;
            }
        }
        return ans;
    };

    React.useEffect(() => {
        ensureArrays();
        rebuildOffsets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, effectiveEstimate]);

    const heightRevision = args.heightRevision ?? 0;
    const heightResetToken = args.heightResetToken;

    /**
     * Copies locked heights into the offset table before spacer math runs.
     * Rows without a lock keep their estimate or last DOM measurement.
     */
    const applyExternalRowHeights = () => {
        const getHeight = getRowHeightRef.current;
        if (!getHeight) {
            return;
        }
        const items = dataRef.current;
        const limit = Math.min(heightsRef.current.length, items.length);
        for (let i = 0; i < limit; i++) {
            const next = getHeight(items[i], i);
            if (next == null || !Number.isFinite(next)) {
                continue;
            }
            heightsRef.current[i] = Math.max(minRowHeight, Math.round(next));
        }
    };

    // Keep spacer math in sync when row count changes (useEffect alone runs one frame late).
    let offsetsStale = heightsRef.current.length !== count;
    if (offsetsStale) {
        ensureArrays();
    }

    if (resetTokenRef.current !== heightResetToken) {
        resetTokenRef.current = heightResetToken;
        heightsRef.current = new Array(count).fill(effectiveEstimate);
        offsetsRef.current = new Array(count).fill(0);
        externalRevisionRef.current = -1;
        offsetsStale = true;
    }

    if (lastEstimateRef.current !== effectiveEstimate && getRowHeightRef.current) {
        const previousEstimate = lastEstimateRef.current;
        lastEstimateRef.current = effectiveEstimate;
        const items = dataRef.current;
        const limit = Math.min(heightsRef.current.length, items.length);
        for (let i = 0; i < limit; i++) {
            const locked = getRowHeightRef.current(items[i], i);
            if (locked != null && Number.isFinite(locked)) {
                continue;
            }
            const current = heightsRef.current[i];
            // Only rows still sitting on the previous estimate move with it.
            // A row measured at the minimum must keep that height.
            if (current === previousEstimate && current !== minRowHeight) {
                heightsRef.current[i] = effectiveEstimate;
                offsetsStale = true;
            }
        }
    } else {
        lastEstimateRef.current = effectiveEstimate;
    }

    if (externalRevisionRef.current !== heightRevision) {
        externalRevisionRef.current = heightRevision;
        applyExternalRowHeights();
        offsetsStale = true;
    }

    if (offsetsStale) {
        rebuildOffsets();
    }

    React.useEffect(() => {
        if (!enabled) return;
        return subscribeToVerticalScrollParent(
            () => {
                const el = getScrollParent();
                scrollParentRef.current = el;
                return el;
            },
            count,
            scrollTopRef,
            clientHeightRef,
            scheduledRef,
            force,
        );
    }, [getScrollParent, count, enabled]);

    const viewportPx = Math.max(
        0,
        clientHeightRef.current - stickyHeaderHeight - stickyFooterHeight - offsetStartingHeight - offsetEndingHeight,
    );
    const startPx = enabled ? Math.max(0, scrollTopRef.current + stickyHeaderHeight - offsetStartingHeight) : 0;
    const endPx = enabled
        ? Math.min(totalHeightRef.current, startPx + viewportPx)
        : totalHeightRef.current;

    let startIndex = 0;
    if (enabled && count > 0) startIndex = findIndexAtOffset(startPx);
    const endIndexByPx = enabled && count > 0 ? Math.min(count, findIndexAtOffset(endPx) + 1) : count;

    const startIndexWithScan = enabled ? Math.max(0, startIndex - overscan) : 0;
    const endIndexWithScan = enabled ? Math.min(count, endIndexByPx + overscan) : count;

    const items = React.useMemo(
        () => data.slice(startIndexWithScan, endIndexWithScan),
        [data, startIndexWithScan, endIndexWithScan],
    );

    React.useEffect(() => {
        if (!enabled || !onRangeChange) return;
        onRangeChange({
            startIndex: startIndexWithScan,
            endIndex: endIndexWithScan,
            startPx,
            endPx,
            totalHeight: totalHeightRef.current,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startIndexWithScan, endIndexWithScan, startPx, endPx]);

    const topSpacerHeight = startIndexWithScan > 0 ? offsetsRef.current[startIndexWithScan] : 0;
    const renderedHeight = sumRange(startIndexWithScan, endIndexWithScan);
    const bottomSpacerHeight = Math.max(0, totalHeightRef.current - topSpacerHeight - renderedHeight);

    const measureRowElement = React.useCallback((el: HTMLElement) => {
        const indexAttr = el.getAttribute(VIRTUAL_ROW_INDEX_ATTR);
        if (indexAttr == null) {
            return;
        }
        const absoluteIndex = Number(indexAttr);
        if (!Number.isFinite(absoluteIndex) || absoluteIndex < 0) {
            return;
        }

        const rect = el.getBoundingClientRect();
        let h = Math.max(minRowHeight, Math.round(rect.height || effectiveEstimate));
        if (preserveLockRef.current && getRowHeightRef.current) {
            const locked = getRowHeightRef.current(dataRef.current[absoluteIndex], absoluteIndex);
            if (locked != null && Number.isFinite(locked)) {
                h = Math.max(h, minRowHeight, Math.round(locked));
            }
        }
        if (heightsRef.current[absoluteIndex] !== h) {
            heightsRef.current[absoluteIndex] = h;
            rebuildOffsets();
            force();
        }
    }, [effectiveEstimate, minRowHeight, rebuildOffsets]);

    const disconnectRowObserver = React.useCallback((absoluteIndex: number) => {
        const ro = rowObserversByIndexRef.current.get(absoluteIndex);
        if (ro) {
            ro.disconnect();
            rowObserversByIndexRef.current.delete(absoluteIndex);
        }
        rowElementsByIndexRef.current.delete(absoluteIndex);
    }, []);

    React.useEffect(() => {
        return () => {
            rowObserversByIndexRef.current.forEach((ro) => ro.disconnect());
            rowObserversByIndexRef.current.clear();
            rowElementsByIndexRef.current.clear();
        };
    }, []);

    const rowRef = (visibleIndex: number) => (el: HTMLElement | null) => {
        if (!enabled) {
            return;
        }
        const absoluteIndex = startIndexWithScan + visibleIndex;

        if (!el) {
            disconnectRowObserver(absoluteIndex);
            return;
        }

        const previousEl = rowElementsByIndexRef.current.get(absoluteIndex);
        if (previousEl && previousEl !== el) {
            disconnectRowObserver(absoluteIndex);
        }

        el.setAttribute(VIRTUAL_ROW_INDEX_ATTR, String(absoluteIndex));
        rowElementsByIndexRef.current.set(absoluteIndex, el);
        measureRowElement(el);

        let ro = rowObserversByIndexRef.current.get(absoluteIndex);
        if (!ro) {
            ro = new ResizeObserver((entries) => {
                for (const ent of entries) {
                    if (ent.target instanceof HTMLElement) {
                        measureRowElement(ent.target);
                    }
                }
            });
            rowObserversByIndexRef.current.set(absoluteIndex, ro);
        }
        ro.disconnect();
        ro.observe(el);
    };

    const keyFor = (visibleIndex: number) => {
        const absoluteIndex = startIndexWithScan + visibleIndex;
        return getItemKey ? getItemKey(data[absoluteIndex], absoluteIndex) : absoluteIndex;
    };

    const scrollToIndex = (index: number, align: ScrollAlign = "auto") => {
        const el = scrollParentRef.current || getScrollParent();
        if (!el || index < 0 || index >= data.length) return;
        const off = offsetsRef.current;
        const hts = heightsRef.current;
        const rowTop = off[index] || 0;
        const rowHeight = Math.max(minRowHeight, hts[index] || effectiveEstimate);
        const currentTop = scrollTopRef.current;
        const viewport = Math.max(
            0,
            clientHeightRef.current - stickyHeaderHeight - stickyFooterHeight - offsetStartingHeight - offsetEndingHeight,
        );
        const rowContentTop = offsetStartingHeight + rowTop;
        const rowContentBottom = rowContentTop + rowHeight;
        const viewStart = currentTop + stickyHeaderHeight;
        const viewEnd = currentTop + clientHeightRef.current - offsetEndingHeight;

        let targetTop = currentTop;
        const fullyVisible = rowContentTop >= viewStart && rowContentBottom <= viewEnd;

        if (align === "auto") {
            if (!fullyVisible) targetTop = rowContentTop - stickyHeaderHeight;
        } else if (align === "start" || align === "belowHeader") {
            targetTop = rowContentTop - stickyHeaderHeight;
        } else if (align === "center") {
            targetTop = rowContentTop - Math.max(0, (viewport - rowHeight) / 2) - stickyHeaderHeight;
        } else if (align === "end") {
            targetTop = rowContentTop - Math.max(0, viewport - rowHeight) - stickyHeaderHeight;
        }

        targetTop = Math.max(0, Math.min(totalHeightRef.current - clientHeightRef.current, targetTop));
        el.scrollTop = targetTop;
    };

    const scrollToKey = (key: string | number, align: ScrollAlign = "auto") => {
        const idx = keyToIndexRef.current.get(key);
        if (idx == null) return;
        scrollToIndex(idx, align);
    };

    const scrollToGroup = (groupKey: string | number, align: ScrollAlign = "belowHeader") => {
        const idx = groupKeyToFirstIndexRef.current.get(groupKey);
        if (idx == null) return;
        scrollToIndex(idx, align);
    };

    const resetAfterIndex = (index: number) => {
        if (index < 0 || index >= heightsRef.current.length) return;
        for (let i = index; i < heightsRef.current.length; i++) {
            heightsRef.current[i] = effectiveEstimate;
        }
        rebuildOffsets();
        force();
    };

    return {
        items,
        startIndex: startIndexWithScan,
        endIndex: endIndexWithScan,
        topSpacerHeight,
        bottomSpacerHeight,
        totalHeight: totalHeightRef.current,
        rowRef,
        keyFor,
        scrollToIndex,
        scrollToKey,
        scrollToGroup,
        resetAfterIndex,
    } as const;
}

/**
 * Column virtualizer for horizontally scrolling tables.
 * Always renders left/right pinned columns; virtualizes the center region.
 */
export function useColumnVirtualizer<TColumn>(args: {
    columns: TColumn[];
    getScrollParent: () => HTMLElement | null;
    getColumnWidth: (col: TColumn) => number;
    isPinnedLeft?: (col: TColumn) => boolean;
    isPinnedRight?: (col: TColumn) => boolean;
    overscanPx?: number;
    /** When false, renders all center columns with no spacers and no scroll subscription. Default true. */
    enabled?: boolean;
}) {
    const {
        columns,
        getScrollParent,
        getColumnWidth,
        isPinnedLeft = () => false,
        isPinnedRight = () => false,
        overscanPx = 240,
    } = args;
    const enabled = args.enabled ?? true;

    const leftPinned = React.useMemo(() => columns.filter(isPinnedLeft), [columns, isPinnedLeft]);
    const rightPinned = React.useMemo(() => columns.filter(isPinnedRight), [columns, isPinnedRight]);
    const leftPinnedWidth = React.useMemo(
        () => leftPinned.reduce((sum, c) => sum + (getColumnWidth(c) || 0), 0),
        [leftPinned, getColumnWidth],
    );
    const rightPinnedWidth = React.useMemo(
        () => rightPinned.reduce((sum, c) => sum + (getColumnWidth(c) || 0), 0),
        [rightPinned, getColumnWidth],
    );

    const centerCols = React.useMemo(
        () => columns.filter((c) => !isPinnedLeft(c) && !isPinnedRight(c)),
        [columns, isPinnedLeft, isPinnedRight],
    );

    const widths = React.useMemo(() => centerCols.map(getColumnWidth), [centerCols, getColumnWidth]);
    const offsets = React.useMemo(() => {
        const off: number[] = new Array(widths.length).fill(0);
        let sum = 0;
        for (let i = 0; i < widths.length; i++) {
            off[i] = sum;
            sum += widths[i] || 0;
        }
        return off;
    }, [widths]);
    const totalCenterWidth = React.useMemo(() => widths.reduce((a, b) => a + (b || 0), 0), [widths]);

    const scrollLeftRef = React.useRef(0);
    const clientWidthRef = React.useRef(0);
    const scheduledRef = React.useRef(false);
    const [, force] = React.useReducer((x) => x + 1, 0);

    React.useEffect(() => {
        if (!enabled) return;
        return subscribeToHorizontalScrollParent(
            getScrollParent,
            scrollLeftRef,
            clientWidthRef,
            scheduledRef,
            force,
        );
    }, [getScrollParent, enabled]);

    if (!enabled) {
        return {
            leftPinned,
            rightPinned,
            visibleCenter: centerCols,
            leftSpacerWidth: 0,
            rightSpacerWidth: 0,
            totalCenterWidth,
            leftPinnedWidth,
            rightPinnedWidth,
        } as const;
    }

    const startPx = Math.max(0, scrollLeftRef.current - overscanPx);
    const endPx = Math.min(totalCenterWidth, scrollLeftRef.current + clientWidthRef.current + overscanPx);

    const findIndexAt = (px: number) => {
        let lo = 0;
        let hi = offsets.length - 1;
        let ans = 0;
        while (lo <= hi) {
            const mid = (lo + hi) >>> 1;
            if (offsets[mid] <= px) {
                ans = mid;
                lo = mid + 1;
            } else {
                hi = mid - 1;
            }
        }
        return ans;
    };

    const startIdx = Math.max(0, findIndexAt(startPx));
    const endIdx = Math.min(widths.length, findIndexAt(endPx) + 1);

    const visibleCenter = centerCols.slice(startIdx, endIdx);
    const leftSpacerWidth = offsets[startIdx] ?? 0;
    const renderedWidth = widths.slice(startIdx, endIdx).reduce((a, b) => a + (b || 0), 0);
    const rightSpacerWidth = Math.max(0, totalCenterWidth - leftSpacerWidth - renderedWidth);

    return {
        leftPinned,
        rightPinned,
        visibleCenter,
        leftSpacerWidth,
        rightSpacerWidth,
        totalCenterWidth,
        leftPinnedWidth,
        rightPinnedWidth,
    } as const;
}
